// AddIGlobalModal.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import {
  PlusCircle,
  Image as ImageIcon,
  Tags,
  DollarSign,
  Info,
  X,
  CheckCircle2,
  Weight as WeightIcon,
  Layers as VariantIcon,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";
import "../Inventory/InventoryAccordion.css";
import "./GlobalInventoryModals.css"
import MyContext from "../../../MyContext";

const AddIGlobalModal = ({
  show,
  onClose,
  adminId,
  baseUrl,
  BASIC_AUTH,
  prefilledCategoryName = "",
  prefilledSubCategoryName = "",
}) => {
  const { config } = useContext(MyContext);

  // Size / Pack options
  const SIZE_OPTIONS = useMemo(
    () => [
      "",
      "Small",
      "Medium",
      "Large",
      "Extra Large",
      "Quarter",
      "Half",
      "Three-Quarter",
      "Full",
      "100g",
      "200g",
      "250g",
      "400g",
      "500g",
      "750g",
      "1kg",
      "2kg",
      "5kg",
      "10kg",
      "100ml",
      "200ml",
      "250ml",
      "500ml",
      "750ml",
      "1L",
      "2L",
      "5L",
      "1 Piece",
      "2 Pieces",
      "3 Pieces",
      "Pack of 6",
      "Pack of 12",
      "Single",
      "Double",
      "Family Pack",
      "Combo",
    ],
    []
  );

  // 🔽 Edit these lists anytime to add/remove categories quickly
  const VARIANT_CATEGORY_GROUPS = useMemo(
    () => [
      {
        label: "Industry",
        options: [
          "Food",
          "Bakery",
          "Grocery",
          "Dairy",
          "Beverages",
          "Household",
          "Personal Care",
          "Pharmacy",
          "Pet Care",
          "Baby Care",
          "Stationery",
          "Home & Kitchen",
          "Cleaning",
          "Electronics",
          "Fashion",
          "Health & Wellness",
        ],
      },
      {
        label: "Food Types",
        options: [
          "Fruits & Vegetables",
          "Staples & Grains",
          "Rice & Flour",
          "Pulses & Lentils",
          "Oils & Ghee",
          "Masala & Spices",
          "Snacks & Namkeen",
          "Biscuits & Cookies",
          "Chocolates & Sweets",
          "Breakfast & Cereals",
          "Instant & Ready-to-Eat",
          "Bakery & Breads",
          "Cakes & Pastries",
          "Dairy & Cheese",
          "Paneer & Tofu",
          "Eggs & Meat",
          "Seafood",
          "Frozen Foods",
          "Beverages - Soft Drinks",
          "Beverages - Juices",
          "Tea & Coffee",
          "Water & Ice",
        ],
      },
    ],
    []
  );

  const [form, setForm] = useState({
    productName: "",
    imageUrl: "",
    size: "",
    price: "",
    category: prefilledCategoryName,
    subCategory: prefilledSubCategoryName,
    barcodeNumber: "",
    variantId: "",
    variantTitle: "",
  });

  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Variants
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [showVariantManager, setShowVariantManager] = useState(false);
  const [variantForm, setVariantForm] = useState({
    variantId: "",
    variantTitle: "",
    category: prefilledCategoryName || "",
  });

  // SWAL z-index fix
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-swal-zfix", "true");
    style.innerHTML = `.swal2-container{z-index:100000!important}`;
    document.head.appendChild(style);
    return () => {
      const el = document.querySelector('style[data-swal-zfix="true"]');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    if (!show) return;
    setForm((f) => ({
      ...f,
      category: prefilledCategoryName || "",
      subCategory: prefilledSubCategoryName || "",
    }));
    setVariantForm((vf) => ({ ...vf, category: prefilledCategoryName || "" }));
    setPreview((p) => (form.imageUrl?.startsWith?.("data:") ? form.imageUrl : ""));
    fetchVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, prefilledCategoryName, prefilledSubCategoryName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "category" || name === "subCategory") return;
    if (name === "variantId") {
      const sel = variants.find((v) => String(v.id) === String(value));
      setForm((f) => ({
        ...f,
        variantId: value,
        variantTitle: sel?.variant_title || "",
      }));
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFileToBase64 = async (file) => {
    if (!file) return;
    const toBase64 = (f) =>
      new Promise((res, rej) => {
        const reader = new FileReader();
        reader.readAsDataURL(f);
        reader.onload = () => res(reader.result);
        reader.onerror = (err) => rej(err);
      });
    try {
      const b64 = await toBase64(file);
      setForm((f) => ({ ...f, imageUrl: b64 }));
      setPreview(b64);
    } catch {
      Swal.fire({ icon: "error", title: "Image Error", text: "Failed to read image file." });
    }
  };

  const uploadImageIfNeeded = async () => {
    if (form.imageUrl && !form.imageUrl.startsWith("data:")) return form.imageUrl || "";
    if (!form.imageUrl?.startsWith?.("data:")) return "";
    const requestBody = {
      userid: (adminId ?? "") || "admin",
      item_name: form.productName || "Item",
      type: "IMG",
      image_data: form.imageUrl,
    };
    const resp = await axios.post(
      "https://bumppy.in/anlook/api/v1/uploadItemImage.php",
      requestBody,
      config
    );
    if (resp?.data?.status === "0" && resp?.data?.image_url) return resp.data.image_url;
    throw new Error(resp?.data?.message || "Image upload failed");
  };

  const postJson = async (url, payload) => {
    if (BASIC_AUTH) {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${BASIC_AUTH}` },
        body: JSON.stringify(payload),
      });
      return res.json();
    }
    const res = await axios.post(url, payload, config);
    return res.data;
  };

  // ------- Variants -------
  const fetchVariants = async () => {
    try {
      setLoadingVariants(true);
      const body = { userid: String(adminId ?? "") };
      const data = await postJson(`${baseUrl}/api/Inventory/v1.0/getAllInventoryVariant`, body);

      // Predicted list (adjust here if keys differ)
      // A) { responseCode:"00", response:[{ pk_variant_id, variant_title, category }]}
      // B) { code:200, data:[{ _id, variantTitle, category }]}
      let list = [];
      if (Array.isArray(data?.response)) {
        list = data.response.map((r) => ({
          id: String(r.pk_variant_id ?? r.variantId ?? r._id ?? ""),
          variant_title: r.variant_title ?? r.variantTitle ?? "",
          category: r.category ?? "",
        }));
      } else if (Array.isArray(data?.data)) {
        list = data.data.map((r) => ({
          id: String(r._id ?? r.variantId ?? r.pk_variant_id ?? ""),
          variant_title: r.variantTitle ?? r.variant_title ?? "",
          category: r.category ?? "",
        }));
      }
      setVariants(list);
    } catch {
      setVariants([]);
    } finally {
      setLoadingVariants(false);
    }
  };

  const createVariant = async () => {
    if (!variantForm.variantTitle.trim()) {
      Swal.fire({ icon: "warning", title: "Variant title required" });
      return;
    }
    const body = {
      variantTitle: variantForm.variantTitle.trim(),
      category: variantForm.category?.trim() || "",
    };
    try {
      const data = await postJson(`${baseUrl}/api/Inventory/v1.0/insertInventoryVariant`, body);
      if (data?.responseCode === "00" || data?.code === 200) {
        Swal.fire({ icon: "success", title: "Variant added" });
        setVariantForm({ variantId: "", variantTitle: "", category: form.category || "" });
        fetchVariants();
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: data?.responseMessage || "Could not add variant." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Could not add variant." });
    }
  };

  const startEditVariant = (v) => {
    setVariantForm({
      variantId: String(v.id),
      variantTitle: v.variant_title || "",
      category: v.category || form.category || "",
    });
  };

  const updateVariant = async () => {
    if (!variantForm.variantId) return;
    if (!variantForm.variantTitle.trim()) {
      Swal.fire({ icon: "warning", title: "Variant title required" });
      return;
    }
    const body = {
      variantId: String(variantForm.variantId),
      variantTitle: variantForm.variantTitle.trim(),
      category: variantForm.category?.trim() || "",
    };
    try {
      const data = await postJson(`${baseUrl}/api/Inventory/v1.0/updateInventoryVariant`, body);
      if (data?.responseCode === "00" || data?.code === 200) {
        Swal.fire({ icon: "success", title: "Variant updated" });
        setVariantForm({ variantId: "", variantTitle: "", category: form.category || "" });
        fetchVariants();
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: data?.responseMessage || "Could not update variant." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Could not update variant." });
    }
  };

  const deleteVariant = async (id) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete variant?",
      text: "This cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!ok.isConfirmed) return;

    const body = { variantId: String(id) };
    try {
      const data = await postJson(`${baseUrl}/api/Inventory/v1.0/deleteInventoryVariant`, body);
      if (data?.responseCode === "00" || data?.code === 200) {
        Swal.fire({ icon: "success", title: "Variant deleted" });
        if (String(form.variantId) === String(id)) setForm((f) => ({ ...f, variantId: "", variantTitle: "" }));
        fetchVariants();
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: data?.responseMessage || "Could not delete variant." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Could not delete variant." });
    }
  };

  // ------- Submit -------
  const validate = () => {
    if (!form.productName.trim()) return "Product Name is required";
    if (!form.category?.trim()) return "Category is required";
    if (!/^\d+(\.\d{1,2})?$/.test(form.price || "")) return "Enter a valid price (max 2 decimals)";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const err = validate();
    if (err) {
      Swal.fire({ icon: "warning", title: "Check fields", text: err });
      return;
    }
    try {
      setSubmitting(true);
      const hostedUrl = await uploadImageIfNeeded();
      const payload = {
        items: [
          {
            // adminId: String(adminId ?? ""), // include only if backend wants it
            category: String(form.category || ""),
            subCategory: String(form.subCategory || ""),
            imageUrl: String(hostedUrl || form.imageUrl || ""),
            productName: String(form.productName || ""),
            weight: String(form.size || ""), // maps select "size" to backend key "weight"
            price: String(form.price || ""),
            barcodeNumber: String(form.barcodeNumber || ""),
            // keep/remove as per backend
            variantId: String(form.variantId || ""),
            variantTitle: String(form.variantTitle || ""),
          },
        ],
      };
      const data = await postJson(`${baseUrl}/api/Inventory/v1.0/insertGlobalInventoryItems`, payload);
      if (data?.responseCode === "00") {
        Swal.fire({
          icon: "success",
          title: "Item Added",
          text: data?.responseMessage || "Inserted successfully.",
          confirmButtonColor: "#01C37D",
        });
        onClose?.();
        setForm({
          productName: "",
          imageUrl: "",
          size: "",
          price: "",
          category: prefilledCategoryName || "",
          subCategory: prefilledSubCategoryName || "",
          barcodeNumber: "",
          variantId: "",
          variantTitle: "",
        });
        setPreview("");
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: data?.responseMessage || "Something went wrong." });
      }
    } catch (err2) {
      Swal.fire({ icon: "error", title: "Error", text: err2?.message || "Could not add item." });
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  // ------- UI helpers (tight & aligned) -------
  const Row = ({ children, cols = 2 }) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: 12,
        alignItems: "center",
      }}
    >
      {children}
    </div>
  );

  const Field = ({ label, required, children, note }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 600 }}>
        {label} {required && <span style={{ color: "#d00" }}>*</span>}
      </label>
      {children}
      {note ? <small style={{ color: "#6b7280" }}>{note}</small> : null}
    </div>
  );

  const Input = (props) => (
    <input
      {...props}
      style={{
        height: 38,
        border: "1px solid #d1d5db",
        borderRadius: 8,
        padding: "0 10px",
        outline: "none",
        background: props.type === "file" ? "transparent" : "#fff",
        ...props.style,
      }}
    />
  );

  const Select = (props) => (
    <select
      {...props}
      style={{
        height: 38,
        border: "1px solid #d1d5db",
        borderRadius: 8,
        padding: "0 8px",
        outline: "none",
        background: "#fff",
        ...props.style,
      }}
    />
  );

  const Section = ({ icon, title, children }) => (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
        {icon}
        <span>{title}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
    </section>
  );

return (
  <div className="global-inventory-modal-overlay">
    <div className="global-inventory-modal">
      {/* Header */}
      <div className="global-inventory-modal-header">
        <div className="global-inventory-modal-title">
          <div className="global-inventory-modal-icon">
            <PlusCircle size={16} />
          </div>
          <h4>Add Global Inventory Item</h4>
        </div>

        <button
          type="button"
          className="global-inventory-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Body */}
      <form onSubmit={handleSubmit} className="global-inventory-modal-body">
        {/* Basic Details */}
        <div className="global-inventory-section">
          <div className="global-inventory-section-head">
            <Info size={16} />
            <span>Basic Details</span>
            <span className="gi-sub">Core item information</span>
          </div>

          <div className="global-inventory-grid-2">
            <div className="global-inventory-field">
              <div className="global-inventory-label">
                Product Name <span className="global-inventory-req">*</span>
              </div>
              <input
                className="global-inventory-input"
                name="productName"
                value={form.productName}
                onChange={handleChange}
                placeholder="e.g., Rajdhani Moong Dal"
              />
            </div>

            <div className="global-inventory-field">
              <div className="global-inventory-label">Weight / Pack Size</div>
              <div className="global-inventory-input-withicon">
                <div className="gi-ico">
                  <WeightIcon size={16} />
                </div>
                <select
                  className="global-inventory-select"
                  name="size"
                  value={form.size}
                  onChange={handleChange}
                >
                  {SIZE_OPTIONS.map((opt, i) => (
                    <option key={i} value={opt}>
                      {opt || "Select"}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="global-inventory-grid-2" style={{ marginTop: 12 }}>
            <div className="global-inventory-field">
              <div className="global-inventory-label">Barcode</div>
              <input
                className="global-inventory-input"
                name="barcodeNumber"
                value={form.barcodeNumber}
                onChange={handleChange}
                placeholder="Scan or type barcode"
                inputMode="numeric"
                autoComplete="off"
              />
            </div>

            <div className="global-inventory-field">
              <div className="global-inventory-label">Variant</div>

              <div className="global-inventory-input-withicon">
                <div className="gi-ico">
                  <VariantIcon size={16} />
                </div>

                <select
                  className="global-inventory-select"
                  name="variantId"
                  value={form.variantId}
                  onChange={handleChange}
                >
                  <option value="">{loadingVariants ? "Loading…" : "Select"}</option>
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.variant_title}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="global-inventory-btn"
                  onClick={() => setShowVariantManager((s) => !s)}
                >
                  <Plus size={16} />
                  Manage
                </button>
              </div>

              {form.variantTitle ? (
                <div className="global-inventory-note">Selected: {form.variantTitle}</div>
              ) : null}
            </div>
          </div>

          {/* Variant Manager */}
          {showVariantManager && (
            <div className="global-inventory-variant-panel">
              <div className="global-inventory-section-head" style={{ marginBottom: 8 }}>
                <VariantIcon size={16} />
                <span>Variant Manager</span>
                <span className="gi-sub">Create / Update / Delete</span>
              </div>

              <div className="global-inventory-grid-3">
                <div className="global-inventory-field">
                  <div className="global-inventory-label">Variant Title</div>
                  <input
                    className="global-inventory-input"
                    value={variantForm.variantTitle}
                    onChange={(e) =>
                      setVariantForm((vf) => ({ ...vf, variantTitle: e.target.value }))
                    }
                    placeholder="e.g., Organic / Sugar Free"
                  />
                </div>

                <div className="global-inventory-field">
                  <div className="global-inventory-label">Category (for this variant)</div>
                  <select
                    className="global-inventory-select"
                    value={variantForm.category}
                    onChange={(e) =>
                      setVariantForm((vf) => ({ ...vf, category: e.target.value }))
                    }
                  >
                    <option value="">Select</option>
                    {VARIANT_CATEGORY_GROUPS.map((grp) => (
                      <optgroup key={grp.label} label={grp.label}>
                        {grp.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div className="global-inventory-field" style={{ justifyContent: "flex-end" }}>
                  {!variantForm.variantId ? (
                    <button
                      type="button"
                      className="global-inventory-btn global-inventory-btn-primary"
                      onClick={createVariant}
                    >
                      <PlusCircle size={16} />
                      Create
                    </button>
                  ) : (
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        type="button"
                        className="global-inventory-btn global-inventory-btn-blue"
                        onClick={updateVariant}
                      >
                        <CheckCircle2 size={16} />
                        Update
                      </button>
                      <button
                        type="button"
                        className="global-inventory-btn"
                        onClick={() =>
                          setVariantForm({
                            variantId: "",
                            variantTitle: "",
                            category: form.category || "",
                          })
                        }
                      >
                        <X size={16} />
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="global-inventory-variant-list">
                {loadingVariants ? (
                  <div className="global-inventory-note">Loading…</div>
                ) : variants.length === 0 ? (
                  <div className="global-inventory-note">No variants yet.</div>
                ) : (
                  variants.map((v) => (
                    <div className="global-inventory-variant-item" key={v.id}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ fontWeight: 900 }}>{v.variant_title}</div>
                        {v.category ? <span className="global-inventory-chip">{v.category}</span> : null}
                      </div>

                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          className="global-inventory-iconbtn"
                          title="Edit"
                          onClick={() => startEditVariant(v)}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="global-inventory-iconbtn danger"
                          title="Delete"
                          onClick={() => deleteVariant(v.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Category */}
        <div className="global-inventory-section">
          <div className="global-inventory-section-head">
            <Tags size={16} />
            <span>Category</span>
            <span className="gi-sub">Prefilled from selection</span>
          </div>

          <div className="global-inventory-grid-2">
            <div className="global-inventory-field">
              <div className="global-inventory-label">
                Category <span className="global-inventory-req">*</span>
              </div>
              <input className="global-inventory-input" value={form.category} readOnly disabled />
            </div>

            <div className="global-inventory-field">
              <div className="global-inventory-label">Sub Category</div>
              <input className="global-inventory-input" value={form.subCategory} readOnly disabled />
            </div>
          </div>
        </div>

        {/* Media */}
        <div className="global-inventory-section">
          <div className="global-inventory-section-head">
            <ImageIcon size={16} />
            <span>Media</span>
            <span className="gi-sub">URL or upload</span>
          </div>

          <div className="global-inventory-grid-2">
            <div className="global-inventory-field">
              <div className="global-inventory-label">Image URL</div>
              <input
                className="global-inventory-input"
                name="imageUrl"
                value={form.imageUrl && !form.imageUrl.startsWith("data:") ? form.imageUrl : ""}
                onChange={(e) => {
                  setForm((f) => ({ ...f, imageUrl: e.target.value }));
                  setPreview("");
                }}
                placeholder="https://…"
              />
            </div>

            <div className="global-inventory-field">
              <div className="global-inventory-label">Or Upload Image</div>
              <input
                className="global-inventory-input"
                type="file"
                accept="image/*"
                onChange={(e) => handleFileToBase64(e.target.files?.[0])}
              />
            </div>
          </div>

          {preview ? (
            <div className="global-inventory-preview">
              <img src={preview} alt="preview" />
            </div>
          ) : null}
        </div>

        {/* Pricing */}
        <div className="global-inventory-section">
          <div className="global-inventory-section-head">
            <DollarSign size={16} />
            <span>Pricing</span>
            <span className="gi-sub">MRP / Selling</span>
          </div>

          <div className="global-inventory-grid-2">
            <div className="global-inventory-field">
              <div className="global-inventory-label">
                Price <span className="global-inventory-req">*</span>
              </div>
              <input
                className="global-inventory-input"
                name="price"
                value={form.price}
                onChange={(e) => {
                  const v = e.target.value
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./g, "$1");
                  setForm((f) => ({ ...f, price: v }));
                }}
                placeholder="e.g., 99.00"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="global-inventory-footer">
          <button
            type="button"
            className="global-inventory-btn"
            onClick={onClose}
            disabled={submitting}
          >
            <X size={16} />
            Cancel
          </button>

          <button
            type="submit"
            className="global-inventory-btn global-inventory-btn-primary"
            disabled={submitting}
          >
            <CheckCircle2 size={16} />
            {submitting ? "Adding…" : "Add Item"}
          </button>
        </div>
      </form>
    </div>
  </div>
);

};

export default AddIGlobalModal;


// import React, { useContext, useEffect, useState } from "react";
// import Swal from "sweetalert2";
// import axios from "axios";
// import {
//   PlusCircle,
//   Image as ImageIcon,
//   Tags,
//   DollarSign,
//   Info,
//   X,
//   CheckCircle2,
//   Weight as WeightIcon,
// } from "lucide-react";
// import "../Inventory/InventoryAccordion.css";
// import MyContext from "../../../MyContext";


// const AddIGlobalModal = ({
//   show,
//   onClose,
//   adminId,
//   baseUrl,
//   BASIC_AUTH,
//   prefilledCategoryName = "",
//   prefilledSubCategoryName = "",
// }) => {
//   const { config } = useContext(MyContext);

//   const [form, setForm] = useState({
//     productName: "",
//     imageUrl: "",
//     weight: "",
//     price: "",
//     category: prefilledCategoryName,
//     subCategory: prefilledSubCategoryName,
//     // NEW: barcode number field (optional)
//     barcodeNumber: "",
//   });

//   const [preview, setPreview] = useState("");
//   const [submitting, setSubmitting] = useState(false);

//   // 🔧 Ensure SweetAlert2 is always on top of any modal overlay
//   useEffect(() => {
//     const style = document.createElement("style");
//     style.setAttribute("data-swal-zfix", "true");
//     style.innerHTML = `
//       .swal2-container { z-index: 100000 !important; }
//       .swal2-container.swal2-top, 
//       .swal2-container.swal2-center, 
//       .swal2-container.swal2-bottom { z-index: 100000 !important; }
//     `;
//     document.head.appendChild(style);
//     return () => {
//       const el = document.querySelector('style[data-swal-zfix="true"]');
//       if (el) el.remove();
//     };
//   }, []);

//   useEffect(() => {
//     if (show) {
//       setForm((f) => ({
//         ...f,
//         category: prefilledCategoryName || "",
//         subCategory: prefilledSubCategoryName || "",
//       }));
//       setPreview((p) =>
//         form.imageUrl && form.imageUrl.startsWith("data:") ? form.imageUrl : ""
//       );
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [show, prefilledCategoryName, prefilledSubCategoryName]);

//   if (!show) return null;

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     if (name === "category" || name === "subCategory") return;
//     setForm((f) => ({ ...f, [name]: value }));
//   };

//   const handleFileToBase64 = async (file) => {
//     if (!file) return;
//     const toBase64 = (f) =>
//       new Promise((res, rej) => {
//         const reader = new FileReader();
//         reader.readAsDataURL(f);
//         reader.onload = () => res(reader.result);
//         reader.onerror = (err) => rej(err);
//       });
//     try {
//       const b64 = await toBase64(file);
//       setForm((f) => ({ ...f, imageUrl: b64 }));
//       setPreview(b64);
//     } catch (err) {
//       console.error("Image encode error:", err);
//       Swal.fire({ icon: "error", title: "Image Error", text: "Failed to read image file." });
//     }
//   };

//   // Optional: upload base64 to get a hosted URL; skip if already a URL
//   const uploadImageIfNeeded = async () => {
//     if (form.imageUrl && !form.imageUrl.startsWith("data:")) return form.imageUrl || "";
//     if (!form.imageUrl || !form.imageUrl.startsWith("data:")) return "";

//     const requestBody = {
//       userid: (adminId ?? "") || "admin",
//       item_name: form.productName || "Item",
//       type: "IMG",
//       image_data: form.imageUrl,
//     };

//     const resp = await axios.post(
//       "https://bumppy.in/anlook/api/v1/uploadItemImage.php",
//       requestBody,
//       config
//     );

//     if (resp?.data?.status === "0" && resp?.data?.image_url) {
//       return resp.data.image_url;
//     } else {
//       throw new Error(resp?.data?.message || "Image upload failed");
//     }
//   };

//   const submitWithAxiosConfig = async (payload) => {
//     const res = await axios.post(
//       `${baseUrl}/api/Inventory/v1.0/insertGlobalInventoryItems`,
//       payload,
//       config
//     );
//     return res.data;
//   };

//   const submitWithFetchBasic = async (payload) => {
//     const res = await fetch(`${baseUrl}/api/Inventory/v1.0/insertGlobalInventoryItems`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Basic ${BASIC_AUTH}`,
//       },
//       body: JSON.stringify(payload),
//     });
//     return res.json();
//   };

//   // ❌ No adminId validation anymore (it can be null)
//   const validate = () => {
//     if (!form.productName.trim()) return "Product Name is required";
//     if (!form.category?.trim()) return "Category is required";
//     if (!form.price?.trim()) return "Price is required";
//     if (!/^\d+(\.\d{1,2})?$/.test(form.price)) return "Enter a valid price (max 2 decimals)";
//     return null;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (submitting) return;

//     const err = validate();
//     if (err) {
//       Swal.fire({ icon: "warning", title: "Check fields", text: err });
//       return;
//     }

//     try {
//       setSubmitting(true);

//       // 1) If base64 image was chosen, upload to get a URL
//       const hostedUrl = await uploadImageIfNeeded();

//       // 2) Build payload (adminId can be empty string if null)
//       const payload = {
//         items: [
//           {
//             // adminId: String(adminId ?? ""),
//             category: String(form.category || ""),
//             subCategory: String(form.subCategory || ""),
//             imageUrl: String(hostedUrl || form.imageUrl || ""),
//             productName: String(form.productName || ""),
//             weight: String(form.weight || ""),
//             price: String(form.price),
//             // NEW: barcodeNumber
//             barcodeNumber: String(form.barcodeNumber || ""),
//           },
//         ],
//       };

//       const data = BASIC_AUTH
//         ? await submitWithFetchBasic(payload)
//         : await submitWithAxiosConfig(payload);

//       if (data?.responseCode === "00") {
//         Swal.fire({
//           icon: "success",
//           title: "Item Added to Global Inventory",
//           text: data?.responseMessage || "New item inserted successfully.",
//           confirmButtonColor: "#2F7D32",
//         });
//         onClose?.();
//         setForm({
//           productName: "",
//           imageUrl: "",
//           weight: "",
//           price: "",
//           category: prefilledCategoryName || "",
//           subCategory: prefilledSubCategoryName || "",
//           barcodeNumber: "", // reset
//         });
//         setPreview("");
//       } else {
//         Swal.fire({
//           icon: "error",
//           title: "Failed",
//           text: data?.responseMessage || "Something went wrong.",
//         });
//       }
//     } catch (err) {
//       console.error(err);
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: err?.message || "Could not add item.",
//       });
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <div className="global-inventory-modal-overlay">
//       <div className="global-inventory-modal inv-itemadd-fixed-size">
//         <div className="global-inventory-modal-header inv-itemadd-head-gradient">
//           <div className="inv-itemadd-head-title">
//             <PlusCircle size={20} />
//             <h4>Add Global Inventory Item</h4>
//           </div>
//           <button className="global-inventory-modal-close inv-itemadd-icon-btn" onClick={onClose} aria-label="Close">
//             <X size={18} />
//           </button>
//         </div>

//         <form onSubmit={handleSubmit} className="inventory-modal-body inv-itemadd-body-scroll">
//           {/* BASIC */}
//           <section className="inv-itemadd-section">
//             <div className="inv-itemadd-section-title">
//               <Info size={16} />
//               <span>Basic Details</span>
//             </div>
//             <div className="inv-itemadd-grid-2">
//               <div className="inv-itemadd-field">
//                 <label>
//                   Product Name <span className="inv-itemadd-req">*</span>
//                 </label>
//                 <input type="text" name="productName" value={form.productName} onChange={handleChange} required />
//               </div>

//               <div className="inv-itemadd-field">
//                 <label>Weight / Pack Size</label>
//                 <div className="inv-itemadd-input-withicon">
//                   <WeightIcon size={16} />
//                   <input
//                     type="text"
//                     name="weight"
//                     placeholder="e.g., 500 g, 1 kg, 750 ml"
//                     value={form.weight}
//                     onChange={handleChange}
//                   />
//                 </div>
//               </div>
//             </div>

//             {/* NEW: Barcode */}
//             <div className="inv-itemadd-grid-2">
//               <div className="inv-itemadd-field">
//                 <label>Barcode</label>
//                 <input
//                   type="text"
//                   name="barcodeNumber"
//                   value={form.barcodeNumber}
//                   onChange={handleChange}
//                   placeholder="Scan or type barcode"
//                   inputMode="numeric"
//                   autoComplete="off"
//                 />
//               </div>
//             </div>
//           </section>

//           {/* CATEGORY */}
//           <section className="inv-itemadd-section">
//             <div className="inv-itemadd-section-title">
//               <Tags size={16} />
//               <span>Category</span>
//             </div>
//             <div className="inv-itemadd-grid-3">
//               <div className="inv-itemadd-field">
//                 <label>
//                   Category <span className="inv-itemadd-req">*</span>
//                 </label>
//                 <input type="text" name="category" value={form.category} readOnly disabled />
//               </div>
//               <div className="inv-itemadd-field">
//                 <label>Sub Category</label>
//                 <input type="text" name="subCategory" value={form.subCategory} readOnly disabled />
//               </div>
//             </div>
//           </section>

//           {/* MEDIA */}
//           <section className="inv-itemadd-section">
//             <div className="inv-itemadd-section-title">
//               <ImageIcon size={16} />
//               <span>Media</span>
//             </div>
//             <div className="inv-itemadd-grid-2">
//               <div className="inv-itemadd-field">
//                 <label>Image URL</label>
//                 <input
//                   type="text"
//                   name="imageUrl"
//                   value={form.imageUrl && !form.imageUrl.startsWith("data:") ? form.imageUrl : ""}
//                   onChange={(e) => {
//                     setForm((f) => ({ ...f, imageUrl: e.target.value }));
//                     setPreview("");
//                   }}
//                   placeholder="https://..."
//                 />
//               </div>
//               <div className="inv-itemadd-field">
//                 <label>Or Upload Image</label>
//                 <input type="file" accept="image/*" onChange={(e) => handleFileToBase64(e.target.files?.[0])} />
//               </div>
//             </div>
//             {preview ? (
//               <div className="inv-itemadd-preview">
//                 <img src={preview} alt="preview" />
//               </div>
//             ) : null}
//           </section>

//           {/* PRICING */}
//           <section className="inv-itemadd-section">
//             <div className="inv-itemadd-section-title">
//               <DollarSign size={16} />
//               <span>Pricing</span>
//             </div>
//             <div className="inv-itemadd-grid-3">
//               <div className="inv-itemadd-field">
//                 <label>
//                   Price <span className="inv-itemadd-req">*</span>
//                 </label>
//                 <input
//                   type="text"
//                   name="price"
//                   value={form.price}
//                   onChange={(e) => {
//                     const v = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
//                     setForm((f) => ({ ...f, price: v }));
//                   }}
//                   placeholder="e.g., 99.00"
//                   required
//                 />
//               </div>
//             </div>
//           </section>

//           {/* FOOTER */}
//           <div className="inventory-modal-footer inv-itemadd-footer-sticky">
//             <button type="button" className="inv-itemadd-outline" onClick={onClose} disabled={submitting}>
//               <X size={16} /> Cancel
//             </button>
//             <button type="submit" className="inv-itemadd-cta" disabled={submitting}>
//               <CheckCircle2 size={16} /> {submitting ? "Adding…" : "Add Item"}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default AddIGlobalModal;
